import useIframeCommunication from '@/composables/use-iframe-communication'
import { unref, ref, computed, watch, type ComputedRef } from 'vue'
import { EXTERNAL_LINKS } from '@/scripts/app-configs/constants'

const MAX_RETRIES_IFRAME_READY = 10
const IFRAME_TIMEOUT_MULTIPLIER = 100

let iFrameNotReadyTimeout: undefined | ReturnType<typeof setTimeout> | number

type memberChatDetailsType = {
  accountUuid?: ComputedRef<string>,
  unit?: ComputedRef<string>,
  channelId?: ComputedRef<string>,
}

type optionsType = {
  enableIframeUrlUpdate: ComputedRef<boolean> | boolean,
}

const defaultOpts: optionsType = {
  enableIframeUrlUpdate: true,
}

export default function useChatIframe(memberChatDetails: memberChatDetailsType, iFrameEventsCallback?: CallableFunction, opts?: optionsType) {

  const options: optionsType = {
    ...defaultOpts,
    ...(opts || {}),
  }

  const baseChatUrl = `${EXTERNAL_LINKS.uiPortal.url}/app/chat?mode=widget`
  const iframeRef = ref<HTMLElement | null>(null)
  const iframeIsReady = ref<boolean>(false)

  const accountUuid = computed(() => memberChatDetails.accountUuid?.value)
  const unit = computed(() => memberChatDetails.unit?.value)
  const channelId = computed(() => memberChatDetails.channelId?.value)

  const { updateIframeRoute } = useIframeCommunication(iFrameEventsCallback)

  const resetSelectedChatChannelUrl = () => {
    postNewUrlToIframe('/app/chat?mode=widget')
  }

  const generateChatUrl = (initialIframeSrc = false): string => {

    const directChannelRouteUrl = `/app/chat/channel/${channelId.value}?mode=widget`
    const channelDiscoverRouteUrl = `/app/chat/discover?mode=widget&accountUuid=${accountUuid.value}&unit=${unit.value}`

    const channelRouteUrl = channelId.value ? directChannelRouteUrl : channelDiscoverRouteUrl

    if (!initialIframeSrc) return channelRouteUrl

    return `${EXTERNAL_LINKS.uiPortal.url}${channelRouteUrl}`
  }

  const postNewUrlToIframe = (url: string, count = 0) => {
    // If already scheduled, clean it
    if (iFrameNotReadyTimeout) window.clearTimeout(iFrameNotReadyTimeout)

    // iFrame already ready
    if (iframeIsReady.value) {
      updateIframeRoute(url, iframeRef.value)

      return
    }

    // iFrame ready event yet to arrive
    // Reached max retries, log error and exit
    if (count >= MAX_RETRIES_IFRAME_READY) {
      console.error('[uml-master-task-chat] Chat iFrame unable to start')

      return
    }

    // Schedule a new URL update
    iFrameNotReadyTimeout = window.setTimeout(() => postNewUrlToIframe(url, count + 1), count * IFRAME_TIMEOUT_MULTIPLIER)
  }

  watch([accountUuid, unit, channelId], () => {
    if (!unref(options.enableIframeUrlUpdate)) return

    const chatUrl = generateChatUrl()

    postNewUrlToIframe(chatUrl)
  })

  return {
    baseChatUrl,
    initialIframeSrc: generateChatUrl(true),
    // Refs
    iframeRef,
    iframeIsReady,
    // Methods
    resetSelectedChatChannelUrl,
  }
}
